import { FocusRow, type RowState } from './V2FocusRow';

/**
 * One Discovery question, inside the shared focused-list chrome.
 *
 * Mirrors V2QuestionRow: FocusRow owns the row, this owns what a Discovery
 * question answers with — a variable list of options that is sometimes
 * multi-select, which is the one way the two screens differ.
 */

/** A question or an escalation check, so one list can hold both. */
export type DiscoveryRowModel = {
  id: string;
  text: string;
  options: { key: string; label: string }[];
  select: 'SINGLE' | 'MULTI';
  maxSelections?: number;
  /** The first of the escalation checks, which carries their heading. */
  startsChecks?: boolean;
};

/** Toggling within a question, honouring its select mode and cap. */
const nextSelection = (
  row: DiscoveryRowModel,
  current: string[],
  key: string,
): string[] | null => {
  if (row.select === 'SINGLE') return [key];
  if (current.includes(key)) return current.filter((value) => value !== key);

  const max = row.maxSelections ?? row.options.length;
  // At the cap, ignore the tap rather than silently dropping an earlier choice:
  // the server rejects an over-cap payload, and quietly swapping an answer the
  // client already made is worse than doing nothing.
  if (current.length >= max) return null;
  return [...current, key];
};

function OptionButton({
  label,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      disabled={disabled}
      className={`rounded-[10px] border px-[13px] py-[11px] text-left text-[14.5px] transition-colors duration-150 motion-reduce:transition-none ${
        selected
          ? 'border-[1.5px] border-[#026857] bg-[#026857]/10 font-semibold text-gray-900'
          : disabled
            ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400'
            : 'border-gray-200 bg-white text-gray-700 hover:border-[#026857]'
      }`}
    >
      {label}
    </button>
  );
}

export function DiscoveryRow({
  row,
  position,
  total,
  state,
  chosen,
  isSaving,
  doneLabel,
  onPick,
  onReopen,
  onDone,
  rowRef,
}: {
  row: DiscoveryRowModel;
  position: number;
  total: number;
  state: RowState;
  chosen: string[];
  isSaving: boolean;
  /** What the multi-select's way out says — it depends on what comes next. */
  doneLabel: string;
  /**
   * The full selection after this tap, not the key that was tapped.
   *
   * What a tap means is the row's to know: whether it replaces or toggles, and
   * whether the cap says it means nothing at all. The screen is then left with
   * what happens after an answer, which is its own question.
   */
  onPick: (keys: string[]) => void;
  onReopen: () => void;
  onDone: () => void;
  rowRef: React.RefObject<HTMLDivElement | null> | undefined;
}) {
  const open = state === 'open';
  const labels = chosen.map(
    (key) => row.options.find((option) => option.key === key)?.label ?? key,
  );
  const max = row.maxSelections ?? row.options.length;
  const atCap = row.select === 'MULTI' && chosen.length >= max;

  return (
    <FocusRow
      question={row.text}
      position={position}
      total={total}
      state={state}
      /*
       * Several choices become "First one +2" rather than a run of comma-joined
       * sentences: three Discovery labels in full are longer than the question
       * they belong to, and the row has one line.
       */
      answerSummary={
        labels.length === 0
          ? undefined
          : labels.length === 1
            ? labels[0]
            : `${labels[0]} +${labels.length - 1}`
      }
      // The abbreviation is for the badge, not for what is announced.
      answerSpoken={labels.join(', ') || undefined}
      isBusy={isSaving}
      onReopen={onReopen}
      rowRef={rowRef}
    >
      {row.select === 'MULTI' && (
        <p className="mt-3 text-xs text-gray-400">
          {row.maxSelections ? `Choose up to ${row.maxSelections}.` : 'Select all that apply.'}
        </p>
      )}
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {row.options.map((option) => (
          <OptionButton
            key={option.key}
            label={option.label}
            selected={chosen.includes(option.key)}
            // Also disabled while the row is closed: the options stay mounted so
            // the row can animate, and a disabled button is out of the tab order.
            disabled={!open || isSaving || (atCap && !chosen.includes(option.key))}
            onClick={() => {
              const next = nextSelection(row, chosen, option.key);
              if (next) onPick(next);
            }}
          />
        ))}
      </div>

      {/* Only a multi-select needs this: nothing else can tell whether the
          client has finished choosing. */}
      {row.select === 'MULTI' && chosen.length > 0 && (
        <button
          type="button"
          disabled={!open || isSaving}
          onClick={onDone}
          className="mt-3 text-[13px] font-semibold text-[#026857] hover:underline"
        >
          {doneLabel}
        </button>
      )}
    </FocusRow>
  );
}
